import commonService from 'utils/commonService';
import apiService from './apiService';
import { BaseRes } from 'bff/models/base/baseRes';
import { MemberProfile, MemberProfiles } from 'bff/models/member';
import { ReturnCode } from 'constants/enums/returnCode.enum';
import _ from 'lodash';
import { SysOptionReq, AgeData, ContractAge } from 'bff/models/info';
import { SysOptType } from 'constants/enums/sysOptType.enum';
import { SysOptions } from 'bff/models/info/sysoptions';

/**
 * @description 檢核帳號是否已存在格式是否正確
 * @param memberId 會員帳號 Id
 * @param applicationId Application Id
 */
const validateExistMemberId = (memberId: string, applicationId?: string) => {
  const reqConfig = commonService.getReqConfig(applicationId);
  const fetchMemberProfileReq = commonService.getApiReqData({ MEMBER_ID: [memberId] });

  return apiService.fetchMemberProfile(fetchMemberProfileReq, reqConfig).then((res: BaseRes<MemberProfile>) => {
    switch (res.MWHEADER.RETURNCODE) {
      case ReturnCode.CodeParamError:
      case ReturnCode.CodeParamFormatError:
        return '格式錯誤';
      case ReturnCode.CodeSuccess:
        return '此帳號已被使用';
      case ReturnCode.CodeNoInfo:
      default:
        return '';
    }
  });
};

/**
 * @description 檢核帳號是否為會員
 * @param memberId 會員帳號 Id
 */
const validMember = (memberId: string) => {
  const reqConfig = commonService.getReqConfig();
  const fetchMemberProfileReq = commonService.getApiReqData({ MEMBER_ID: [memberId] });

  return apiService.fetchMemberProfile(fetchMemberProfileReq, reqConfig).then((res: BaseRes<MemberProfile>) => {
    switch (res.MWHEADER.RETURNCODE) {
      case ReturnCode.CodeSuccess:
        return res.MWHEADER.RETURNDESC;
      case ReturnCode.CodeNoInfo:
      default:
        return '';
    }
  });
};

/**
 * @description 身份證檢核(獨立)-共用
*/
const validMemberId = (memberId: string, nationalityType?: string) => {
  const baseReq = commonService.getApiReqData({ ID: memberId, ID_TYPE: nationalityType });
  const reqConfig = commonService.getReqConfig();
  return apiService.validMemberId(baseReq, reqConfig).then((res: BaseRes<any>) => {
    if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
      const result = {
        ID: res.TRANRS.ID ? res.TRANRS.ID : res.MWHEADER.RETURNDESC
      };
      return result;
    } else {
      const result = {
        ID: '',
        NATIVE: res.TRANRS.NATIVE
      };
      return result;
    }
  });
};

/**
 * @description 會員輸入資料格式檢查
 * @param memberProfile 會員資料
 * @param applicationId Application Id
 * @returns 回傳錯誤訊息物件或空物件
*/
const memberCheckfield = async (memberProfile: MemberProfile, applicationId?: string) => {
  // console.log('memberCheckfield ', memberProfile);
  const baseReq = commonService.getApiReqData({ MEMBER: memberProfile });
  const reqConfig = commonService.getReqConfig(applicationId);
  const res = await apiService.memberCheckfield(baseReq, reqConfig).then((res: BaseRes<any>) => res);

  if (res.MWHEADER.RETURNCODE === ReturnCode.CodeMobileOrEmailRepeat) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
    return;
  }

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    return { ...res.TRANRS.MEMBER };
  }

  return { ...res.TRANRS };
};

/**
 * @description 取得會員資料
 * @param memberId 會員登入帳號
 */
const fetchMemberProfile = async (memberId: string) => {
  const reqConfig = commonService.getReqConfig();
  const fetchMemberProfileReq = commonService.getApiReqData({ MEMBER_ID: memberId });

  const result = await apiService.fetchMemberProfile(fetchMemberProfileReq, reqConfig).then((res: BaseRes<MemberProfiles>) => res);

  if (result.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    // sweetAlertService.error('無法取得會員資料');
    commonService.showErrorModal('無法取得會員資料', result.MWHEADER.RETURNCODE);
  } else {
    const memberProfile = _.find(result.TRANRS.MEMBER, (memberProfile: MemberProfile) => memberProfile.MEMBER_ID === memberId);
    return memberProfile;
  }
};

/**
 * @description 取得系統參數資料
*/
const fetchSysOpts = async (applicationId?: string) => {
  const sysOptionReq: SysOptionReq = {
    PCLASS: [SysOptType.GENDER, SysOptType.MARRIAGE]
  };

  const sysOptionBaseReq = commonService.getApiReqData(sysOptionReq);
  const reqConfig = commonService.getReqConfig(applicationId);

  const sysOptionBaseRes: BaseRes<SysOptions> = await apiService.fetchSysOptions(sysOptionBaseReq, reqConfig).then((res: BaseRes<SysOptions>) => res);

  if (sysOptionBaseRes.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(sysOptionBaseRes.MWHEADER.RETURNDESC, sysOptionBaseRes.MWHEADER.RETURNCODE);
  } else {
    const res = {
      genderSysOpts: commonService.sortSysOpts(commonService.arrayTypeReturn(sysOptionBaseRes.TRANRS[SysOptType.GENDER])),
      marriageSysOpts: commonService.sortSysOpts(commonService.arrayTypeReturn(sysOptionBaseRes.TRANRS[SysOptType.MARRIAGE]))
    };

    return res;
  }
};

/**
 * @description 檢核保險年齡
*/
const validContractAge = async (ageData: AgeData[]) => {
  const baseReq = commonService.getApiReqData({ AGE_DATA: ageData });
  const reqConfig = commonService.getReqConfig();

  const res: BaseRes<ContractAge> = await apiService.validContractAge(baseReq, reqConfig).then((res: BaseRes<ContractAge>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return [...res.TRANRS.AGE_DATA];
  }
};

export default {
  validateExistMemberId,
  memberCheckfield,
  fetchMemberProfile,
  fetchSysOpts,
  validMemberId,
  validContractAge,
  validMember
};
